import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert, Box, Button } from '@factorialco/deprecated-design-system'
import {
  DimensionImportDataDocument,
  FinanceGetDimensionValuesDocument,
  ImportCustomDimensionMembershipsDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FileUploader from 'modules/finance/components/FileUploader'
import Loading from 'modules/finance/components/Loading'

interface Props {}

const DimensionsImportPage = ({}: Props) => {
  const { id } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const customDimensionId = Number(id!)

  const { data, error } = useQuery(DimensionImportDataDocument, {
    variables: {
      tenant: { companyId },
      customDimensionId: customDimensionId,
    },
  })

  const [importFinanceCustomDimensionMemberships, { loading }] = useMutation(
    ImportCustomDimensionMembershipsDocument,
    {
      extractorFn: (data) => data.financeMutations.importFinanceCustomDimensionMembership,
      refetchQueries: [
        {
          query: FinanceGetDimensionValuesDocument,
          variables: {
            dimensionId: customDimensionId,
            tenant: { companyId: companyId },
          },
        },
      ],
    }
  )

  const handleDrop = (files: File[]) => {
    setSelectedFile(files[0])
  }

  const isLegalEntityAvailable = (legalEntityId: number) => {
    return (
      data?.employees.employees.some((employee) => employee.legalEntity?.id === legalEntityId) &&
      data?.finance.customDimensionValues.some(
        (dimension) => !dimension.legalEntity || dimension.legalEntity.id === legalEntityId
      )
    )
  }

  const dimensionName = data?.finance.customDimension?.name
  const legalEntities = data?.apiCore.currents[0]?.company.legalEntities

  const showAlert = legalEntities?.some((le) => !isLegalEntityAvailable(le.id))

  const onSubmit = async () => {
    const result = await importFinanceCustomDimensionMemberships({
      variables: {
        customDimensionId: customDimensionId,
        file: selectedFile,
        tenant: { companyId: companyId },
      },
    })

    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('finance.dimension_detail.import_modal.error'),
      })
      track(trackingEvents.accounting.bulkAssignmentSubmit, {
        status: 'error',
        type: 'custom',
        name: dimensionName,
      })
    })

    if (handled.success) {
      const successfulImports = handled.data.customDimensionMemberships?.length || 0
      const message = i18n.tp('finance.dimension_detail.import_modal.success', {
        count: successfulImports,
      })

      app.setFlash({
        type: 'correct',
        message: message,
      })
      track(trackingEvents.accounting.bulkAssignmentSubmit, {
        status: 'success',
        type: 'custom',
        name: dimensionName,
      })
      onClose()
    }
  }

  const onClose = () => {
    navigate(navigation.settings.finance.dimensions.dimension.fullPath({ id: id! }))
  }

  return (
    <Modal
      title={
        dimensionName
          ? i18n.t('finance.dimension_detail.import_modal.title', {
              dimension_name: dimensionName,
            })
          : ''
      }
      description={i18n.t('finance.dimension_detail.import_modal.description')}
      illustration="list-document"
      footer={
        <>
          <Button.Router
            to={navigation.settings.finance.dimensions.dimension.fullPath({ id: id! })}
            type="secondary"
          >
            {i18n.t('common.cancel')}
          </Button.Router>
          <Button submit disabled={!selectedFile || loading}>
            {i18n.t(
              'finance.modals.import_cost_center_assignment.existing_cost_centers.buttons.import'
            )}
          </Button>
        </>
      }
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <Loading loading={!data && !error}>
        <Box gap="s16">
          {showAlert && (
            <Alert color="warning">
              {i18n.t('finance.dimension_detail.import_modal.warning_alert')}
            </Alert>
          )}
          <Box gap="s16" alignItems="center">
            {legalEntities?.map((legalEntity) => (
              <Button.Router
                key={legalEntity.id}
                to={buildUrl(
                  `/finance/exports/dimension_assignments?legal_entity_id=${legalEntity.id}&company_id=${companyId}&dimension_id=${customDimensionId}`
                )}
                disabled={!isLegalEntityAvailable(legalEntity.id)}
                type="secondary"
                onClick={() => {
                  track(trackingEvents.accounting.bulkAssigmentTemplateDownload, {
                    status: 'success',
                    legal_entity_name: legalEntity.legalName,
                    type: 'custom',
                    name: dimensionName,
                  })
                }}
                iconName="download"
              >
                {legalEntities.length === 1
                  ? i18n.t(
                      'finance.modals.import_cost_center_assignment.existing_cost_centers.buttons.download_template'
                    )
                  : legalEntity.legalName}
              </Button.Router>
            ))}
          </Box>
          <Box gap="s16" paddingY="s8" flexGrow>
            <FileUploader
              placeholder={i18n.t(
                'finance.modals.import_cost_center_assignment.existing_cost_centers.dropzone.placeholder'
              )}
              description={i18n.t(
                'finance.modals.import_cost_center_assignment.existing_cost_centers.dropzone.allowed_formats'
              )}
              selectedFile={selectedFile}
              onDrop={handleDrop}
              acceptedFormats={{
                ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']: ['.xlsx'],
              }}
            />
          </Box>
        </Box>
      </Loading>
    </Modal>
  )
}

export default DimensionsImportPage
